import { useCallback, useEffect, useMemo } from 'react';
import { Elements, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  Stripe,
  StripeElements,
  StripeElementsOptions,
  StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';

export type StripeLoader = Stripe | null | PromiseLike<Stripe | null>;

interface WalletExpressCheckoutProps {
  stripe: StripeLoader;
  amountMinor: number;
  currency: string;
  disabled: boolean;
  onAvailabilityChange: (available: boolean) => void;
  onConfirm: (stripe: Stripe, elements: StripeElements) => Promise<void>;
}

/**
 * Apple Pay / Google Pay rendered by Stripe's current wallet integration.
 *
 * This owns a separate deferred-mode Elements group. The checkout's outer group must stay in its
 * existing shape because split Card Elements and MB WAY's Payment Element deliberately use
 * different configurations. Keeping the wallet group isolated also lets it stay mounted while its
 * official button is moved in and out of the footer.
 */
export function WalletExpressCheckout({
  stripe,
  amountMinor,
  currency,
  disabled,
  onAvailabilityChange,
  onConfirm,
}: WalletExpressCheckoutProps) {
  /**
   * A zero or negative total means the price never arrived, not that the purchase is free.
   * Deferred-mode Elements rejects a non-positive amount, so the group must not be mounted at all.
   *
   * Reported as unavailable rather than left at whatever the last render said, and logged: the
   * symptom is a silently missing wallet row, which is indistinguishable from "this device cannot
   * pay". `MewsPaymentStep` passes `amountMajor ?? 0`, so this is reachable in ordinary use.
   */
  const chargeable = amountMinor > 0;

  useEffect(() => {
    if (chargeable) return;
    console.warn(
      `WalletExpressCheckout: wallet row hidden — total is ${amountMinor}. ` +
        'The purchase has no price, so no wallet sheet can be opened for it.'
    );
    onAvailabilityChange(false);
  }, [chargeable, amountMinor, onAvailabilityChange]);

  const elementsOptions = useMemo<StripeElementsOptions>(
    () => ({
      mode: 'payment',
      amount: amountMinor,
      currency: currency.toLowerCase(),
      // Apple Pay and Google Pay are card-backed in Stripe — the docs are explicit that both are
      // "automatically enabled when using `card` payment method type", and neither is a payment
      // method type of its own here. The actual charged amount is still resolved server-side when
      // the PaymentIntent is created after wallet authorisation.
      paymentMethodTypes: ['card'],
    }),
    [amountMinor, currency]
  );

  if (!chargeable) return null;

  return (
    <Elements stripe={stripe} options={elementsOptions}>
      <WalletExpressElement
        disabled={disabled}
        onAvailabilityChange={onAvailabilityChange}
        onConfirm={onConfirm}
      />
    </Elements>
  );
}

interface WalletExpressElementProps {
  disabled: boolean;
  onAvailabilityChange: (available: boolean) => void;
  onConfirm: (stripe: Stripe, elements: StripeElements) => Promise<void>;
}

function WalletExpressElement({ disabled, onAvailabilityChange, onConfirm }: WalletExpressElementProps) {
  const stripe = useStripe();
  const elements = useElements();

  const options = useMemo<StripeExpressCheckoutElementOptions>(
    () => ({
      paymentMethods: {
        applePay: 'auto',
        googlePay: 'auto',
        // A row labelled Apple Pay / Google Pay must never open checkout.link.com.
        link: 'never',
        paypal: 'never',
        amazonPay: 'never',
        klarna: 'never',
      },
      paymentMethodOrder: ['apple_pay', 'google_pay'],
      buttonType: { applePay: 'check-out', googlePay: 'checkout' },
      buttonHeight: 48,
      /*
        One column, stacked, and never an overflow menu.

        `maxRows: 0` means *unlimited* rows, and it is the only value Stripe allows alongside
        `overflow: 'never'` — "You can't specify both `overflow: 'never'` and set `maxRows` to a
        number greater than 0." A number here throws `IntegrationError` before the element mounts,
        which is invisible: the throw is an unhandled rejection, so `onLoadError` never fires, no
        availability is ever reported, and the row simply does not appear. Unlimited costs nothing —
        only Apple Pay and Google Pay are enabled below, so at most two buttons can ever stack.
      */
      layout: { maxColumns: 1, maxRows: 0, overflow: 'never' },
    }),
    []
  );

  /**
   * Passes Stripe's verdict up, and says out loud what that verdict was.
   *
   * Without this the only symptom of a hidden row is its absence, and the causes need entirely
   * different fixes: the payment method domain has to be registered for this exact origin **and in
   * the same mode as the publishable key**, the wallet has to be enabled on the Stripe account, and
   * the browser has to support it at all — Chrome on Android never offers Apple Pay, only Google Pay.
   * Reading the payload Stripe actually sent is what separates those without a device in hand.
   *
   * `ready` legitimately reports nothing and is then superseded by `availablepaymentmethodschange`,
   * so a warning followed by an availability line is a normal sequence, not a contradiction.
   *
   * The engine facts travel with the verdict because an unregistered domain and an engine that
   * cannot pay at all produce the identical symptom — a null payload — and need opposite fixes.
   * `PaymentRequest` is what Stripe drives Google Pay from and an Android WebView does not expose
   * it, so `paymentRequest=undefined` alongside a `wv` user agent means the build is the wrong
   * place to test wallets, whatever the Stripe dashboard says. Inlined here rather than logged
   * separately so a single `adb logcat` line carries the whole diagnosis: on device the console is
   * usually read through logcat, where correlating two lines is needless work.
   */
  const reportAvailability = useCallback(
    (available: boolean, field: string, methods: unknown) => {
      const engine =
        `paymentRequest=${typeof window.PaymentRequest} ` +
        `secureContext=${window.isSecureContext} ua=${navigator.userAgent}`;
      const detail = `origin=${window.location.origin} ${field}=${JSON.stringify(methods ?? null)} ${engine}`;
      if (available) {
        console.info(`WalletExpressCheckout: wallet available — ${detail}`);
      } else {
        console.warn(
          `WalletExpressCheckout: no wallet available, row hidden — ${detail}. Check the payment ` +
            'method domain is registered for this origin in the same mode as the publishable key, ' +
            'and that the wallet is enabled on the Stripe account.'
        );
      }
      onAvailabilityChange(available);
    },
    [onAvailabilityChange]
  );

  const handleReady = useCallback(
    (event: StripeExpressCheckoutElementReadyEvent) => {
      const methods = event.availablePaymentMethods;
      reportAvailability(
        Boolean(methods?.applePay || methods?.googlePay),
        'availablePaymentMethods',
        methods
      );
    },
    [reportAvailability]
  );

  const handleAvailablePaymentMethodsChange = useCallback(
    (event: StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent) => {
      const methods = event.paymentMethods;
      reportAvailability(
        Boolean(methods?.applePay?.available || methods?.googlePay?.available),
        'paymentMethods',
        methods
      );
    },
    [reportAvailability]
  );

  const handleConfirm = useCallback(async () => {
    // The wrapper is inert while terms are unticked, but keep the business gate here too so a
    // synthetic/stale event can never create an intent behind the disabled UI.
    if (disabled || !stripe || !elements) return;
    await onConfirm(stripe, elements);
  }, [disabled, elements, onConfirm, stripe]);

  return (
    <ExpressCheckoutElement
      options={options}
      onReady={handleReady}
      onAvailablePaymentMethodsChange={handleAvailablePaymentMethodsChange}
      onConfirm={handleConfirm}
      onLoadError={event => {
        console.warn('WalletExpressCheckout: Express Checkout Element failed to load:', event.error);
        onAvailabilityChange(false);
      }}
    />
  );
}
